import { featureCollection } from '@turf/turf';
import { useContext, useEffect, useMemo } from 'react';
import {
  largePointRadius,
  mapLayers,
  mapModes,
  smallPointRadius,
  SOIL_BOUNDARIES_LAYER_ID,
} from '../../constants/map';
import { useItemContext } from '../../contexts/ItemContext';
import { MapContext } from '../../contexts/MapContext';
import { emptyGeojson } from '../../utils/geojson';
import {
  SECONDARY_FEATURE_TYPE,
  unaugmented,
} from '../../utils/layerAugmenters';
import { getLightness, MAP_ACTIVE_COLOR } from '../../utils/colors';
import { getCustomLayerColor, layerIsCustom } from '../../utils/layers';
import { geometryTypes } from '../../constants/fields';
import { CUSTOM_LAYER_FILL_OPACITY } from '../../constants/layers';

const customPolygonLayer = (layerId) => `${layerId}-polys`;
const customLineLayer = (layerId) => `${layerId}-lines`;
const customPointLayer = (layerId) => `${layerId}-points`;

// Color for circle outline to make points more visible
const getCircleOutlineColor = (color) => {
  const lightness = getLightness(color);
  return lightness > 0.8 ? 'black' : 'white';
};

// Layer/style filters
const isPoint = ['==', '$type', geometryTypes.POINT];
const isLine = ['==', '$type', geometryTypes.LINE_STRING];
const isPolygon = ['==', '$type', geometryTypes.POLYGON];
const isNotSecondaryFeature = ['none', ['==', 'type', SECONDARY_FEATURE_TYPE]];
const isPointOrLine = ['any', isPoint, isLine];

// Filter shown layer features
const filterLayerFeatures = (layer) => {
  if (!layer?.data?.features) return layer;
  return {
    ...layer,
    data: {
      ...layer.data,
      ...featureCollection(
        layer.data.features.filter(
          (feature) => layer.featuresShown[feature.properties.id],
        ),
      ),
    },
  };
};

const UserLayer = ({ map, layer }) => {
  const {
    layerId,
    featuresShown,
    shown,
    data,
    paintProperties = {},
    augment = unaugmented,
    width,
    disableUserEdits,
    drawTool,
    mapIndexLayer,
  } = layer;

  const color = getCustomLayerColor(layer);
  const isCustom = layerIsCustom(layer);

  const {
    mapState: {
      activeLayerId,
      activeLayerFeatureId,
      selectedLayerFeatureIds,
      mapLoaded,
      setMapMode,
    },
  } = useContext(MapContext);
  const { activeKey, setActiveKey, clearActiveKey } = useItemContext(); // Hovered item

  const shouldEnableClickHandlers = mapLoaded && activeLayerId === layerId;

  const interactionLayerIds = [
    `${layerId}_HOVER_LINE`, // 'line' layer for point & line features
    `${layerId}_HOVER_FILL`, // 'fill' layer for polygons55
  ];

  useEffect(() => {
    if (!shouldEnableClickHandlers) return null;

    const onFeatureClick = (e) => {
      const { id } = e.features[0].properties;
      clearActiveKey();
      // If command is pressed, toggle layer features in selection
      if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
        setMapMode(mapModes.MULTI_LAYER_FEATURE_SELECT, id, {
          selectActiveLayerFeature: true,
        });
      } else {
        setMapMode(mapModes.LAYER_FEATURE_ENGAGED, id);
      }
    };

    const onFeatureDblclick = (e) => {
      if (disableUserEdits || !drawTool) return;
      e.preventDefault();
      const { id } = e.features[0].properties;
      setMapMode(mapModes.EDIT_LAYER_FEATURE, id);
    };

    const onMouseMove = (e) => {
      const { id } = e.features[0].properties;
      if (id) {
        setActiveKey(id);
      } else {
        clearActiveKey();
      }
    };

    interactionLayerIds.forEach((id) => {
      // Set/clear active key when feature is hovered.
      map.on('mousemove', id, onMouseMove);
      map.on('mouseleave', id, clearActiveKey);
      // Engage feature on single click.
      map.on('click', id, onFeatureClick);
      // Edit feature on double click.
      map.on('dblclick', id, onFeatureDblclick);
    });

    return () => {
      if (!map || map._removed) return; // eslint-disable-line no-underscore-dangle

      interactionLayerIds.forEach((id) => {
        map.off('mousemove', id, onMouseMove);
        map.off('mouseleave', id, clearActiveKey);
        map.off('click', id, onFeatureClick);
        map.off('dblclick', id, onFeatureDblclick);
      });
    };
  }, [layerId, shouldEnableClickHandlers]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFeatures = useMemo(() => {
    if (!shown) return featureCollection([]);
    const features = featureCollection(
      layer.data?.features?.filter(
        (feature) =>
          featuresShown[feature.properties.id] &&
          (activeKey === feature.properties.id ||
            activeLayerFeatureId === feature.properties.id ||
            selectedLayerFeatureIds.has(feature.properties.id)),
      ) || [],
    );
    return layerId === 'HEADLANDS'
      ? augment({ data: features, width })
      : features;
  }, [
    layer.data,
    layerId,
    augment,
    width,
    activeLayerFeatureId,
    activeKey,
    featuresShown,
    selectedLayerFeatureIds,
    shown,
  ]);

  const activeFeatureLayerName = `${layerId}_FEATURE`;
  const soilSymbolLayerId = `${layerId}_SYMBOL`;

  useEffect(() => {
    const augmentedData = augment(filterLayerFeatures(layer));

    map.addSource(layerId, {
      type: 'geojson',
      data: augmentedData,
    });
    map.addSource(activeFeatureLayerName, emptyGeojson());

    if (isCustom) {
      /**
       * NOTE: for custom layers, we need to add 3 layers:
       * - a 'fill' layer for the insides of polygon features
       * — a 'line' layer for line features and for the borders of polygon features
       * - a 'circle' layer for point features
       *
       * ALSO: the points layer gets put on top of sections so it's more visible.
       */
      map.addLayer(
        {
          id: customPolygonLayer(layerId),
          source: layerId,
          type: 'fill',
          layout: {},
          paint: {
            'fill-color': color,
            'fill-opacity': CUSTOM_LAYER_FILL_OPACITY,
          },
          filter: ['all', isPolygon],
        },
        mapIndexLayer || mapLayers.OVER_FILL,
      );
      map.addLayer(
        {
          id: customLineLayer(layerId),
          source: layerId,
          type: 'line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': color,
            'line-width': 1.5,
          },
          filter: ['any', isLine, isPolygon],
        },
        mapIndexLayer || mapLayers.OVER_FILL,
      );
      map.addLayer(
        {
          id: customPointLayer(layerId),
          source: layerId,
          type: 'circle',
          paint: {
            'circle-radius': smallPointRadius,
            'circle-color': color,
            // Add an outline to make the points more visible.
            'circle-stroke-color': getCircleOutlineColor(color),
            'circle-stroke-width': 1.5,
            'circle-opacity': 1,
          },
          filter: ['all', isPoint],
        },
        mapIndexLayer || mapLayers.CUSTOM_LAYER_POINTS,
      );
    } else {
      map.addLayer(
        {
          id: layerId,
          source: layerId,
          ...paintProperties,
        },
        mapIndexLayer || mapLayers.OVER_FILL,
      );
    }

    // Interaction layer (click/hover): line layer for point & line features
    map.addLayer(
      {
        id: interactionLayerIds[0],
        source: layerId,
        type: 'line',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          // Use wider lines so it's easier to hover:
          'line-width': 10,
          // Don't show the line, this layer is just for hover/click events.
          'line-opacity': 0,
        },
        filter: ['all', isPointOrLine, isNotSecondaryFeature],
      },
      // (no layer index here, so this ends up on top)
    );

    // Interaction layer (click/hover): fill layer for polygon features
    map.addLayer(
      {
        id: interactionLayerIds[1],
        source: layerId,
        type: 'fill',
        layout: {},
        paint: {
          // Don't show the fill, this layer is just for hover/click events.
          'fill-opacity': 0,
        },
        filter: isPolygon,
      },
      // (no layer index here, so this ends up on top)
    );

    // Layer(s) to show "active" feature
    if (isCustom) {
      map.addLayer(
        {
          id: customLineLayer(activeFeatureLayerName),
          source: activeFeatureLayerName,
          type: 'line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': MAP_ACTIVE_COLOR,
            'line-width': 3,
          },
          filter: ['none', isPoint],
        },
        mapLayers.ACTIVE_LAYER_FEATURE,
      );
      map.addLayer(
        {
          id: customPointLayer(activeFeatureLayerName),
          source: activeFeatureLayerName,
          type: 'circle',
          paint: {
            'circle-radius': largePointRadius,
            'circle-color': MAP_ACTIVE_COLOR,
          },
          filter: ['all', isPoint],
        },
        mapLayers.ACTIVE_LAYER_FEATURE,
      );
    } else {
      map.addLayer(
        {
          id: activeFeatureLayerName,
          type: 'line',
          source: activeFeatureLayerName,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': MAP_ACTIVE_COLOR,
            'line-width': [
              'match',
              // Use the original line width for secondary features (the outer lines for headlands & roads)
              ['get', 'type'],
              SECONDARY_FEATURE_TYPE,
              paintProperties['line-width'] || 1.5,
              3,
            ],
          },
        },
        mapLayers.ACTIVE_LAYER_FEATURE,
      );
      // Symbol layer to display the map unit symbols of soil features
      if (layerId === SOIL_BOUNDARIES_LAYER_ID) {
        map.addLayer(
          {
            id: soilSymbolLayerId,
            type: 'symbol',
            source: layerId,
            layout: {
              'text-field': ['get', 'mapUnitSymbol'],
              'symbol-placement': 'point',
              'text-allow-overlap': true,
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-size': 10,
            },
            paint: {
              'text-halo-color': 'rgba(0,0,0,1)',
              'text-halo-width': 2,
              'text-color': '#fff',
            },
          },
          mapLayers.NAME,
        );
      }
    }

    return () => {
      // eslint-disable-next-line no-underscore-dangle
      if (map && !map._removed) {
        interactionLayerIds.forEach((id) => {
          map.removeLayer(id);
        });

        if (isCustom) {
          map.removeLayer(customPolygonLayer(layerId));
          map.removeLayer(customLineLayer(layerId));
          map.removeLayer(customPointLayer(layerId));
        } else {
          map.removeLayer(layerId);
        }

        map.removeSource(layerId);

        if (isCustom) {
          map.removeLayer(customLineLayer(activeFeatureLayerName));
          map.removeLayer(customPointLayer(activeFeatureLayerName));
        } else {
          map.removeLayer(activeFeatureLayerName);
          if (layerId === SOIL_BOUNDARIES_LAYER_ID) {
            map.removeLayer(soilSymbolLayerId);
          }
        }
        map.removeSource(activeFeatureLayerName);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    map.getSource(layerId).setData(augment(filterLayerFeatures(layer)));
    // The dependencies of this useEffect are properties of layer. We want to
    // update the map layer data when any of these values change, since they
    // affect if and how the layer is rendered on the map.
    // - data: contains the features to be drawn
    // - featuresShown: affects the filterLayerFeatures function
    // - width: affects the augment function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, featuresShown, width]);

  useEffect(() => {
    if (!isCustom) return;

    map.setPaintProperty(customPolygonLayer(layerId), 'fill-color', color);
    map.setPaintProperty(customLineLayer(layerId), 'line-color', color);
    map.setPaintProperty(customPointLayer(layerId), 'circle-color', color);
    map.setPaintProperty(
      customPointLayer(layerId),
      'circle-stroke-color',
      getCircleOutlineColor(color),
    );
  }, [isCustom, color, layerId, map]);

  useEffect(() => {
    if (layerIsCustom(layer)) {
      map.setLayoutProperty(
        customPolygonLayer(layerId),
        'visibility',
        shown ? 'visible' : 'none',
      );
      map.setLayoutProperty(
        customLineLayer(layerId),
        'visibility',
        shown ? 'visible' : 'none',
      );
      map.setLayoutProperty(
        customPointLayer(layerId),
        'visibility',
        shown ? 'visible' : 'none',
      );
    } else {
      const visibility = shown ? 'visible' : 'none';
      map.setLayoutProperty(layerId, 'visibility', visibility);
      if (layerId === SOIL_BOUNDARIES_LAYER_ID) {
        map.setLayoutProperty(soilSymbolLayerId, 'visibility', visibility);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  useEffect(() => {
    map.getSource(activeFeatureLayerName).setData(activeFeatures);
  }, [activeFeatureLayerName, activeFeatures, map]);

  return null;
};

export default UserLayer;
